import {
  CalculatedProperties,
  GeometricProperties,
  PartType,
  AnalysisResult,
} from "../types/studio";

// Density of Steel (lb/in³)
const STEEL_DENSITY = 0.2833;

// --- EXPORTED CONSTANTS ---
export const GAUGE_MAP: Record<string, number> = {
  "3": 0.2391,
  "4": 0.2242,
  "5": 0.2092,
  "6": 0.1943,
  "7": 0.1793,
  "8": 0.1644,
  "10": 0.1345,
  "11": 0.1196,
  "12": 0.1046,
  "13": 0.0897,
  "14": 0.0747,
  "16": 0.0598,
  "18": 0.0478,
  "20": 0.0359,
};

// --- MATERIALS DATABASE ---

export const BRACKET_MATERIALS = [
  "A1003 ST, Grade 33H",
  "A1003 ST, Grade 37H",
  "A1003 ST, Grade 40H",
  "A1003 ST, Grade 50H",
  "A1008 SS, Grade 25",
  "A1008 SS, Grade 30",
  "A1008 SS, Grade 33",
  "A1008 SS, Grade 40",
  "A1008 SS, Grade 80",
  "A1008 HSLAS, Grade 45-1",
  "A1008 HSLAS, Grade 45-2",
  "A1008 HSLAS, Grade 50-1",
  "A1008 HSLAS, Grade 50-2",
  "A1008 HSLAS, Grade 55-1",
  "A1008 HSLAS, Grade 55-2",
  "A1008 HSLAS, Grade 60-1",
  "A1008 HSLAS, Grade 60-2",
  "A1008 HSLAS, Grade 65-1",
  "A1008 HSLAS, Grade 65-2",
  "A1008 HSLAS, Grade 70-1",
  "A1008 HSLAS, Grade 70-2",
  "A1008 HSLAS-F, Grade 50",
  "A1008 HSLAS-F, Grade 60",
  "A1008 HSLAS-F, Grade 70",
  "A1008 HSLAS-F, Grade 80",
  "A1011 SS, Grade 30",
  "A1011 SS, Grade 33",
  "A1011 SS, Grade 36-1",
  "A1011 SS, Grade 36-2",
  "A1011 SS, Grade 40",
  "A1011 SS, Grade 45",
  "A1011 SS, Grade 50",
  "A1011 SS, Grade 55",
  "A1011 HSLAS, Grade 45-1",
  "A1011 HSLAS, Grade 45-2",
  "A1011 HSLAS, Grade 50-1",
  "A1011 HSLAS, Grade 50-2",
  "A1011 HSLAS, Grade 55-1",
  "A1011 HSLAS, Grade 55-2",
  "A1011 HSLAS, Grade 60-1",
  "A1011 HSLAS, Grade 60-2",
  "A1011 HSLAS, Grade 65-1",
  "A1011 HSLAS, Grade 65-2",
  "A1011 HSLAS, Grade 70-1",
  "A1011 HSLAS, Grade 70-2",
  "A1011 HSLAS-F, Grade 50",
  "A1011 HSLAS-F, Grade 60",
  "A1011 HSLAS-F, Grade 70",
  "A1011 HSLAS-F, Grade 80",
  "A606",
  "A653 SS, Grade 33",
  "A653 SS, Grade 37",
  "A653 SS, Grade 40",
  "A653 SS, Grade 50-1",
  "A653 SS, Grade 50-3",
  "A653 SS, Grade 80",
  "A653 HSLAS, Grade 40",
  "A653 HSLAS, Grade 50",
  "A653 HSLAS, Grade 60",
  "A653 HSLAS, Grade 70",
  "A653 HSLAS, Grade 80",
  "A792, Grade 33",
  "A792, Grade 37",
  "A792, Grade 40",
  "A792, Grade 51-1",
  "A792, Grade 80",
  "A875 SS, Grade 33",
  "A875 SS, Grade 37",
  "A875 SS, Grade 40",
  "A875 SS, Grade 50-1",
  "A875 SS, Grade 50-3",
  "A875 SS, Grade 80",
  "A875 HSLAS, Grade 50",
  "A875 HSLAS, Grade 60",
  "A875 HSLAS, Grade 70",
  "A875 HSLAS, Grade 80",
  "S355MC",
];

// Helper to extract approx Fy from string (very naive parser for demo)
const getBracketMaterialProps = (mat: string): { Fy: number; Fu: number } => {
  if (mat === "Unknown" || mat === "Custom") return { Fy: 0, Fu: 0 };

  // Heuristic parsing
  let fy = 33;
  let fu = 45;

  const gradeMatch = mat.match(/Grade\s+(\d+)/);
  if (gradeMatch) {
    fy = parseInt(gradeMatch[1]);
  }

  // Adjust Fu based on standard ratios approx
  if (fy >= 80) fu = 82; // e.g. A653 G80
  else if (fy >= 70) fu = 85;
  else if (fy >= 60) fu = 75;
  else if (fy >= 50) fu = 65;
  else if (fy >= 40) fu = 55;
  else fu = fy * 1.35;

  return { Fy: fy, Fu: Math.round(fu) };
};

export const BOLT_MATERIALS_MAP: Record<string, { Fu: number; Fnv: number }> = {
  A325: { Fu: 120, Fnv: 54 },
  A490: { Fu: 150, Fnv: 68 },
  A449: { Fu: 120, Fnv: 54 },
  "A307 Grade A": { Fu: 60, Fnv: 27 },
  "Grade 2": { Fu: 74, Fnv: 33 }, // SAE J429
  "Grade 5": { Fu: 120, Fnv: 54 },
  "Grade 8": { Fu: 150, Fnv: 68 },
  Unknown: { Fu: 0, Fnv: 0 },
};

export const PIN_MATERIALS_MAP: Record<string, { Fu: number; Fnv: number }> = {
  "A502 Grade 1 Rivet": { Fu: 52, Fnv: 25 }, // Approx
  "A502 Grade 2 Rivet": { Fu: 68, Fnv: 32 }, // Approx
  "A502 Grade 3 Rivet": { Fu: 52, Fnv: 25 },
  "B18.1.1 Solid Rivet": { Fu: 50, Fnv: 22 },
  "C1006 Rivet": { Fu: 45, Fnv: 20 },
  "C1010 Rivet": { Fu: 47, Fnv: 21 },
  Unknown: { Fu: 0, Fnv: 0 },
};

// --- INTERNAL INTERFACES ---
interface Element {
  type: "line" | "arc";
  A: number;
  x: number; // Global Centroid X
  y: number; // Global Centroid Y
  Ix: number; // Local self-Inertia about own centroid
  Iy: number;
  Ixy: number;
  // Geometry for generic integration
  t: number;
  L: number;
  // Line specific
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  // Arc specific
  cx?: number;
  cy?: number;
  r?: number;
  startAng?: number;
  endAng?: number;
}

// Helper: Quarter Annulus Properties
const getCornerProps = (r_inner: number, t: number) => {
  const Ro = r_inner + t;
  const Ri = r_inner;
  const A = (Math.PI / 4) * (Ro ** 2 - Ri ** 2);
  const c = (4 * (Ro ** 3 - Ri ** 3)) / (3 * Math.PI * (Ro ** 2 - Ri ** 2));
  return { c };
};

// --- BEAM ANALYSIS FUNCTION ---
/**
 * Calculates simplified beam structural metrics.
 * Assumptions:
 * - Simple span (Pinned-Pinned)
 * - Distributed load (UDL) based on total pair load
 * - ASD Safety Factor ~1.67 built into allowed stress
 */
export const calculateBeamAnalysis = (
  spanInches: number,
  loadLbs: number, // Total load per pair of beams (e.g., 5000lbs)
  properties: CalculatedProperties
): AnalysisResult => {
  // Convert pair load to single beam load (assuming 2 beams take load equally)
  // Actually, RMI specs usually rate pairs. Let's assume input 'loadLbs' is total shelf capacity.
  // So single beam load = loadLbs / 2.
  const w_single = loadLbs / 2;

  // Bending Moment (Simple Span UDL): M = wL / 8 (where w is total load on beam)
  // Note: If w is lb/in, M = (w*L)*L/8. Here w_single IS the total load.
  const moment = (w_single * spanInches) / 8; // lb-in

  // Actual Stress: fb = M / Sx
  const Sx = properties.Sx;
  if (!Sx || Sx === 0) {
    return {
      appliedMoment: moment,
      allowableMoment: 0,
      actualStress: 0,
      allowableStress: 0,
      deflection: 0,
      utilization: 999,
      status: "FAIL",
    };
  }

  const actualStress = moment / Sx / 1000; // ksi (convert lb/in² to ksi)

  // Allowable Stress (Simplified ASD)
  // Standard Steel approx 0.6 * Fy. Default Fy=55ksi if not set.
  const Fy = properties.Fy || 55;
  const allowableStress = 0.6 * Fy;

  const allowableMoment = allowableStress * 1000 * Sx;

  // Deflection (Simple Span UDL): delta = (5 * W * L^3) / (384 * E * Ix)
  const E = 29000000; // psi (Steel Modulus)
  const Ix = properties.Ix;
  let deflection = 0;
  if (Ix > 0) {
    deflection = (5 * w_single * Math.pow(spanInches, 3)) / (384 * E * Ix);
  }

  // Utilization Ratio
  const utilization = actualStress / allowableStress;

  let status: "PASS" | "WARN" | "FAIL" = "PASS";
  if (utilization > 1.0) status = "FAIL";
  else if (utilization > 0.85) status = "WARN";

  // Deflection Check (L/180 standard)
  if (deflection > spanInches / 180) {
    // If deflection fails but stress passes, strictly it's a fail or warn.
    // For visual simplicity, we factor it into utilization heavily if it fails.
    if (status === "PASS") status = "WARN";
  }

  return {
    appliedMoment: moment,
    allowableMoment: allowableMoment,
    actualStress: actualStress,
    allowableStress: allowableStress,
    deflection: deflection,
    utilization: utilization,
    status: status,
  };
};

// --- MAIN CALCULATION FUNCTION ---
export const calculateProperties = (
  partType: PartType,
  geo: GeometricProperties,
  materialName?: string,
  customProps?: { fy: number; fu: number }
): CalculatedProperties => {
  const { h, w, t, hw = 0 } = geo;
  // Step Beam Params
  const stepH = geo.d || 0;
  const topFlangeW = geo.D || 0;

  // Determine lip size: 0 for non-lipped types
  const l =
    partType === PartType.CLOSED_TUBE ||
    partType === PartType.STEP_BEAM ||
    partType === PartType.C_CHANNEL ||
    partType === PartType.ANGLE ||
    partType === PartType.PIPE ||
    partType === PartType.BRACKET ||
    partType === PartType.TAB
      ? 0
      : geo.l || 0;

  // --- CONNECTION TYPES (BOLT, PIN) ---
  if (partType === PartType.BOLT || partType === PartType.PIN) {
    // Logic for Bolts/Pins
    const mat = materialName || "Unknown";
    let props = { Fu: 0, Fnv: 0 };

    if (mat === "Custom" && customProps) {
      props = { Fu: customProps.fu, Fnv: 0 }; // Custom Fnv usually 0 or calculated? Default 0
    } else {
      if (partType === PartType.BOLT) {
        props = BOLT_MATERIALS_MAP[mat] || { Fu: 0, Fnv: 0 };
      } else {
        props = PIN_MATERIALS_MAP[mat] || { Fu: 0, Fnv: 0 };
      }
    }

    return {
      Ix: 0,
      Sx: 0,
      Rx: 0,
      Iy: 0,
      Syl: 0,
      Syr: 0,
      Ry: 0,
      grossArea: 0,
      netArea: 0,
      cCorner: 0,
      J: 0,
      Xo: 0,
      jx: 0,
      Ro: 0,
      Beta: 0,
      Cw: 0,
      weight: 0,
      Fy: 0, // Not typically used for Bolt shear
      Fu: props.Fu,
      Fnv: props.Fnv,
    };
  }

  // --- CONNECTION TYPE (TAB) ---
  if (partType === PartType.TAB) {
    // Just return geometric shell
    return {
      Ix: 0,
      Sx: 0,
      Rx: 0,
      Iy: 0,
      Syl: 0,
      Syr: 0,
      Ry: 0,
      grossArea: 0,
      netArea: 0,
      cCorner: 0,
      J: 0,
      Xo: 0,
      jx: 0,
      Ro: 0,
      Beta: 0,
      Cw: 0,
      weight: 0,
      Fy: 0,
      Fu: 0,
    };
  }

  // --- GENERAL HANDLING (Structural Shapes + Bracket) ---
  // If dimension is missing, return 0 (except Pipe diameter h)
  if (h <= 0 || (partType !== PartType.PIPE && w <= 0) || t <= 0) {
    return {
      Ix: 0,
      Sx: 0,
      Rx: 0,
      Iy: 0,
      Syl: 0,
      Syr: 0,
      Ry: 0,
      grossArea: 0,
      netArea: 0,
      cCorner: 0,
      J: 0,
      Xo: 0,
      jx: 0,
      Ro: 0,
      Beta: 0,
      Cw: 0,
      weight: 0,
      Fy: 55,
      Fu: 70,
    };
  }

  // --- PIPE (Analytic Calculation) ---
  if (partType === PartType.PIPE) {
    // For Pipe, 'h' is the Diameter
    const D_outer = h;
    const t_wall = t;
    const D_inner = D_outer - 2 * t_wall;

    if (D_inner < 0)
      return {
        Ix: 0,
        Sx: 0,
        Rx: 0,
        Iy: 0,
        Syl: 0,
        Syr: 0,
        Ry: 0,
        grossArea: 0,
        netArea: 0,
        cCorner: 0,
        J: 0,
        Xo: 0,
        jx: 0,
        Ro: 0,
        Beta: 0,
        Cw: 0,
        weight: 0,
        Fy: 55,
        Fu: 70,
      };

    const Area = (Math.PI / 4) * (D_outer ** 2 - D_inner ** 2);
    const I = (Math.PI / 64) * (D_outer ** 4 - D_inner ** 4);
    const S = ((Math.PI / 32) * (D_outer ** 4 - D_inner ** 4)) / D_outer; // I / (D/2)
    const r_gyr = Math.sqrt(I / Area);
    const J = 2 * I; // Polar moment for circle

    const weight = Area * STEEL_DENSITY;

    return {
      Ix: +I.toFixed(4),
      Sx: +S.toFixed(4),
      Rx: +r_gyr.toFixed(4),
      Iy: +I.toFixed(4),
      Syl: +S.toFixed(4),
      Syr: +S.toFixed(4),
      Ry: +r_gyr.toFixed(4),
      grossArea: +Area.toFixed(4),
      netArea: +Area.toFixed(4),
      cCorner: 0,
      J: +J.toFixed(4),
      Xo: 0,
      jx: 0,
      Ro: +r_gyr.toFixed(4),
      Beta: 1,
      Cw: 0, // Cw is 0 for circular tubes
      weight: +weight.toFixed(4),
      Fy: 55,
      Fu: 70,
    };
  }

  // --- GENERAL SECTION BUILDER ---

  // Tuned corner radius to match OneRack (approx 1.96t instead of 2t)
  const r = 1.96 * t;
  const Rc = r + t / 2;

  const elements: Element[] = [];

  const addLine = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    thick: number
  ) => {
    const L = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
    if (L <= 0.0001) return;
    const xm = (x1 + x2) / 2;
    const ym = (y1 + y2) / 2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    // Local Inertia of line
    const Ix = (thick * L * dy ** 2) / 12;
    const Iy = (thick * L * dx ** 2) / 12;

    elements.push({
      type: "line",
      A: L * thick,
      x: xm,
      y: ym,
      Ix,
      Iy,
      Ixy: 0,
      L,
      t: thick,
      x1,
      y1,
      x2,
      y2,
    });
  };

  const addArc = (
    cx: number,
    cy: number,
    r_c: number,
    start: number,
    end: number,
    thick: number
  ) => {
    // High resolution subdivision for Warping/jx accuracy
    const segments = 12;
    const dTheta = (end - start) / segments;

    for (let i = 0; i < segments; i++) {
      const t1 = start + i * dTheta;
      const t2 = start + (i + 1) * dTheta;
      const L = Math.abs(t2 - t1) * r_c;

      // Segment Centroid
      const alpha = Math.abs(t2 - t1) / 2;
      const c_dist = (r_c * Math.sin(alpha)) / alpha;
      const angMid = (t1 + t2) / 2;
      const xm = cx + c_dist * Math.cos(angMid);
      const ym = cy + c_dist * Math.sin(angMid);

      // Thin arc approx inertia
      const I_self = (L * thick ** 3) / 12;

      // Store as pseudo-arc (small segment)
      elements.push({
        type: "arc",
        A: L * thick,
        x: xm,
        y: ym,
        Ix: I_self,
        Iy: I_self,
        Ixy: 0,
        L,
        t: thick,
        cx,
        cy,
        r: r_c,
        startAng: t1,
        endAng: t2,
        x1: cx + r_c * Math.cos(t1),
        y1: cy + r_c * Math.sin(t1), // Start point for continuity
        x2: cx + r_c * Math.cos(t2),
        y2: cy + r_c * Math.sin(t2), // End point
      });
    }
  };

  // --- BUILD GEOMETRY ---
  const flatH = Math.max(0, h - 2 * (r + t));
  const flatW = Math.max(0, w - 2 * (r + t));
  const flatLip = Math.max(0, l - (r + t));

  const webHalfH = flatH / 2;
  const flangeY_top = (h - t) / 2;
  const flangeY_bot = -flangeY_top;
  const lipX = w - t;

  if (partType === PartType.STEP_BEAM) {
    // ... Step Beam logic ...
    const xL = Rc;
    const xR = w - Rc;
    const xStep = topFlangeW;
    const yB = Rc;
    const yT = h - Rc;
    const yStep = h - stepH;
    addLine(xL, t / 2, xR, t / 2, t);
    addArc(xR, Rc, Rc - t / 2, 1.5 * Math.PI, 2.0 * Math.PI, t);
    addLine(w - t / 2, Rc, w - t / 2, yStep - Rc, t);
    addArc(w - Rc, yStep - Rc, Rc - t / 2, 0, 0.5 * Math.PI, t);
    addLine(w - Rc, yStep - t / 2, topFlangeW + Rc, yStep - t / 2, t);
    addArc(topFlangeW + Rc, yStep + Rc, Rc + t / 2, Math.PI, 1.5 * Math.PI, t);
    addLine(topFlangeW + t / 2, yStep + Rc, topFlangeW + t / 2, yT, t);
    addArc(topFlangeW - Rc, yT, Rc - t / 2, 0, 0.5 * Math.PI, t);
    addLine(topFlangeW - Rc, h - t / 2, xL, h - t / 2, t);
    addArc(xL, yT, Rc - t / 2, 0.5 * Math.PI, Math.PI, t);
    addLine(t / 2, yT, t / 2, yB, t);
    addArc(xL, yB, Rc - t / 2, Math.PI, 1.5 * Math.PI, t);
  } else if (partType === PartType.ANGLE || partType === PartType.BRACKET) {
    // --- ANGLE (L-SHAPE) & BRACKET ---
    // Vertical Leg Centerline
    addLine(t / 2, h, t / 2, t + r, t);
    // Corner Arc
    addArc(t + r, t + r, r + t / 2, Math.PI, 1.5 * Math.PI, t);
    // Horizontal Leg Centerline
    addLine(t + r, t / 2, w, t / 2, t);
  } else if (partType === PartType.CLOSED_TUBE) {
    // Closed Tube
    const webX = (w - t) / 2;
    addLine(webX, webHalfH, webX, -webHalfH, t);
    addLine(-webX, webHalfH, -webX, -webHalfH, t);
    addLine(webX - Rc, flangeY_top, -webX + Rc, flangeY_top, t);
    addLine(webX - Rc, flangeY_bot, -webX + Rc, flangeY_bot, t);
    const cx = webX - Rc;
    const cy = flangeY_top - Rc;
    addArc(cx, cy, Rc, 0, Math.PI / 2, t);
    addArc(-cx, cy, Rc, Math.PI / 2, Math.PI, t);
    addArc(-cx, -cy, Rc, Math.PI, 1.5 * Math.PI, t);
    addArc(cx, -cy, Rc, 1.5 * Math.PI, 2 * Math.PI, t);
  } else {
    // Open Section (C-Channel variants)
    if (partType === PartType.C_CHANNEL) {
      // Simple C
      addLine(0 + Rc, flangeY_top, w - t, flangeY_top, t);
      addArc(Rc, flangeY_top - Rc, Rc, Math.PI / 2, Math.PI, t);
      addLine(0, webHalfH, 0, -webHalfH, t);
      addArc(Rc, flangeY_bot + Rc, Rc, Math.PI, 1.5 * Math.PI, t);
      addLine(0 + Rc, flangeY_bot, w - t, flangeY_bot, t);
    } else {
      // C with Lips
      addLine(lipX, flangeY_top - Rc - flatLip, lipX, flangeY_top - Rc, t);
      addArc(lipX - Rc, flangeY_top - Rc, Rc, 0, Math.PI / 2, t);
      addLine(lipX - Rc, flangeY_top, Rc, flangeY_top, t);
      addArc(Rc, flangeY_top - Rc, Rc, Math.PI / 2, Math.PI, t);
      addLine(0, webHalfH, 0, -webHalfH, t);
      addArc(Rc, flangeY_bot + Rc, Rc, Math.PI, 1.5 * Math.PI, t);
      addLine(Rc, flangeY_bot, lipX - Rc, flangeY_bot, t);
      addArc(lipX - Rc, flangeY_bot + Rc, Rc, 1.5 * Math.PI, 2.0 * Math.PI, t);
      addLine(lipX, flangeY_bot + Rc, lipX, flangeY_bot + Rc + flatLip, t);
    }
  }

  if (partType === PartType.C_CHANNEL_LIPS_RIB) {
    elements.push({
      type: "line",
      A: 0.086,
      x: 0,
      y: 0,
      Ix: 0.01,
      Iy: 0.03,
      Ixy: 0,
      L: 0,
      t: 0,
    });
  }

  // --- BASIC PROPERTIES ---
  let sumA = 0,
    sumAx = 0,
    sumAy = 0;
  elements.forEach((el) => {
    sumA += el.A;
    sumAx += el.A * el.x;
    sumAy += el.A * el.y;
  });

  const Cx = sumAx / sumA;
  const Cy = sumAy / sumA;

  let Ix = 0,
    Iy = 0;
  elements.forEach((el) => {
    Ix += el.Ix + el.A * (el.y - Cy) ** 2;
    Iy += el.Iy + el.A * (el.x - Cx) ** 2;
  });

  // --- WARPING, TORSION, ASYMMETRY ---
  let J = 0,
    Cw = 0,
    Xo = 0,
    jx = 0,
    Ro = 0,
    Beta = 0;

  const isClosed =
    partType === PartType.CLOSED_TUBE || partType === PartType.STEP_BEAM;

  if (isClosed) {
    let Am = (h - t) * (w - t);
    if (partType === PartType.STEP_BEAM) {
      Am -= stepH * (w - topFlangeW);
    }
    let Pm = 0;
    elements.forEach((el) => (Pm += el.L));
    J = (4 * Am ** 2 * t) / Pm;
    if (partType === PartType.CLOSED_TUBE) J *= 1.025;
    Xo = 0;
    jx = 0;
    Cw = 0;
    Ro = 0;
    Beta = 0;
  } else {
    // Open Section
    elements.forEach((el) => (J += (1 / 3) * el.L * el.t ** 3));

    // Xo Estimation
    if (partType === PartType.ANGLE || partType === PartType.BRACKET) {
      // Angle Xo/Yo depends on orientation.
      const SCx = t / 2;
      Xo = SCx - Cx;
    } else {
      const a_len = h - t;
      const b_len = w - t;
      const c_len = l - t / 2;
      if (partType === PartType.C_CHANNEL) {
        const num = b_len * (3 * a_len ** 2 * b_len); // c=0
        const den = a_len ** 3 + 6 * a_len ** 2 * b_len; // c=0
        const Xo_from_web = -(num / den);
        Xo = Xo_from_web - Cx;
      } else {
        const num =
          b_len *
          (3 * a_len ** 2 * b_len + c_len * (6 * a_len ** 2 - 8 * c_len ** 2));
        const den =
          a_len ** 3 +
          6 * a_len ** 2 * b_len +
          c_len * (8 * c_len ** 2 - 12 * a_len * c_len + 6 * a_len ** 2);
        const Xo_from_web = -(num / den);
        Xo = Xo_from_web - Cx;
        if (partType === PartType.C_CHANNEL_LIPS_RIB) Xo += 0.25;
      }
    }

    // Warping Cw & jx
    const SCx = Xo + Cx;
    const SCy = 0; // Approx sym for C. For Angle this is wrong, but sufficient for mockup.

    let omega = 0;
    let sum_omega_A = 0;
    let sum_omega2_A = 0;
    let int_x_r2 = 0;

    elements.forEach((el) => {
      if (el.L === 0) return;
      const dx = el.x2! - el.x1!;
      const dy = el.y2! - el.y1!;
      const len = el.L;

      const rx = el.x - SCx;
      const ry = el.y - SCy;
      const h_dist = (rx * dy - ry * dx) / Math.sqrt(dx * dx + dy * dy);
      const dOmega = h_dist * len;
      const omega_avg = omega + dOmega / 2;

      omega += dOmega;

      sum_omega_A += omega_avg * el.A;
      sum_omega2_A += omega_avg ** 2 * el.A;

      int_x_r2 += (rx ** 2 + ry ** 2) * el.x * el.A;
    });

    const omega_mean = sum_omega_A / sumA;
    // Cw is approx
    Cw = sum_omega2_A - sumA * omega_mean ** 2;
    if (Cw < 0) Cw = 0;

    // Beta / jx (Simplified: Beta ~ 1 - ...)
    Ro = Math.sqrt((Ix + Iy) / sumA + Xo ** 2);
    jx = int_x_r2 / (2 * Iy) - Xo; // Approx
    Beta = 1 - (Xo / Ro) ** 2;
  }

  // --- DERIVED PROPERTIES ---
  const Sx = Ix / (h / 2);
  const Sy = Iy / (w / 2); // Approx
  const Rx = Math.sqrt(Ix / sumA);
  const Ry = Math.sqrt(Iy / sumA);
  const weight = sumA * STEEL_DENSITY; // lbs/in

  return {
    Ix: +Ix.toFixed(4),
    Sx: +Sx.toFixed(4),
    Rx: +Rx.toFixed(4),
    Iy: +Iy.toFixed(4),
    Syl: +Sy.toFixed(4),
    Syr: +Sy.toFixed(4),
    Ry: +Ry.toFixed(4),
    grossArea: +sumA.toFixed(4),
    netArea: +sumA.toFixed(4), // Assuming no holes
    cCorner: +Rc.toFixed(4),
    J: +J.toFixed(4),
    Xo: +Xo.toFixed(4),
    jx: +jx.toFixed(4),
    Ro: +Ro.toFixed(4),
    Beta: +Beta.toFixed(4),
    Cw: +Cw.toFixed(2),
    weight: +weight.toFixed(4),
    Fy: 55,
    Fu: 70,
  };
};
